import jsPDF from 'jspdf'
import 'jspdf-autotable'

// Extend jsPDF with autoTable
declare module 'jspdf' {
    interface jsPDF {
        autoTable: (options: any) => jsPDF
    }
}

interface PDFOptions {
    companyName?: string
    companyAddress?: string
    companyEmail?: string
    companyWebsite?: string
}

// Define the types based on the actual data structure
interface Assembly {
    id: string
    name: string
    orgID: string
    startDate: number
}

interface PayoutInvoice {
    id: string
    orgName: string
    billedAssemblyLines: Assembly[]
    billedAssemblyLine: Assembly
    billableAmount: number
    billableUnits: number
    billingEnd: string
    billingStart: string
    details: string
    invoiceLink: string
    resourceID: string
    resourceName?: string
    status: string
    payoutPlatform: string
    statusMessage: string
    timestamp: number
    transferID: string
    message?: string
    // Additional fields that might be available
    invoiceNumber?: string
    type?: string
    paymentTerms?: string
    issueDate?: number
    dueDate?: number
    billingPeriodStart?: string
    billingPeriodEnd?: string
    paidDate?: number
    subtotalAmount?: number
    taxAmount?: number
    totalAmount?: number
    paidAmount?: number
    remainingAmount?: number
    description?: string
    notes?: string
    lineItems?: any[]
    payments?: any[]
    resource?: {
        firstName?: string
        lastName?: string
        email?: string
    }
    assemblyLine?: {
        name?: string
        projectType?: string
        status?: string
    }
}

const defaultOptions: PDFOptions = {
    companyName: 'Assembly Industries',
    companyAddress: '16192 Coastal Highway, Lewes, Delaware 19958, USA',
    companyEmail: 'cs@assembly-industries.com',
    companyWebsite: 'www.assembly-industries.com',
}

export class ContractorInvoicePDFGenerator {
    private doc: jsPDF
    private options: PDFOptions
    private currentY: number = 0
    private pageWidth: number
    private margin: number = 20
    private lineHeight: number = 4
    private sectionSpacing: number = 6
    private leftColumnX: number = 20
    private rightColumnX: number = 110

    constructor(options: PDFOptions = {}) {
        this.options = { ...defaultOptions, ...options }
        this.doc = new jsPDF('p', 'mm', 'a4')
        this.pageWidth = this.doc.internal.pageSize.getWidth()
        this.currentY = this.margin
    }

    private addHeaderSection(invoice: PayoutInvoice): void {
        // Company name and logo on the left
        this.doc.setFontSize(16)
        this.doc.setFont('helvetica', 'bold')
        this.doc.setTextColor(41, 128, 185)
        this.doc.text('ASSEMBLY INDUSTRIES', this.leftColumnX, this.currentY)

        this.currentY += 5

        // Company contact info
        this.doc.setFontSize(8)
        this.doc.setFont('helvetica', 'normal')
        this.doc.setTextColor(52, 73, 94)
        this.doc.text(this.options.companyAddress!, this.leftColumnX, this.currentY)
        this.currentY += this.lineHeight

        this.doc.text(`Email: ${this.options.companyEmail}`, this.leftColumnX, this.currentY)
        this.currentY += this.lineHeight
        this.doc.text(`Website: ${this.options.companyWebsite}`, this.leftColumnX, this.currentY)

        // Invoice title and details on the right (aligned with company name)
        this.doc.setFontSize(18)
        this.doc.setFont('helvetica', 'bold')
        this.doc.setTextColor(41, 128, 185)
        this.doc.text('CONTRACTOR INVOICE', this.rightColumnX, this.margin)

        this.doc.setFontSize(9)
        this.doc.setFont('helvetica', 'normal')
        this.doc.setTextColor(52, 73, 94)

        this.doc.text(`Invoice #: ${invoice.invoiceNumber || invoice.id || 'N/A'}`, this.rightColumnX, this.margin + 6)
        this.doc.text(`Status: ${invoice.status || 'N/A'}`, this.rightColumnX, this.margin + 10)
        this.doc.text(`Type: ${invoice.type || 'CONTRACTOR'}`, this.rightColumnX, this.margin + 14)
        this.doc.text(`Payment Terms: ${invoice.paymentTerms || 'NET_30'}`, this.rightColumnX, this.margin + 18)

        this.currentY += this.sectionSpacing
    }

    private addContractorAndProjectSection(invoice: PayoutInvoice): void {
        // Contractor Information on the left
        this.doc.setFontSize(10)
        this.doc.setFont('helvetica', 'bold')
        this.doc.setTextColor(52, 73, 94)
        this.doc.text('Contractor Information:', this.leftColumnX, this.currentY)

        this.currentY += 4

        this.doc.setFontSize(8)
        this.doc.setFont('helvetica', 'normal')

        if (invoice.resourceName) {
            this.doc.text(invoice.resourceName, this.leftColumnX, this.currentY)
            this.currentY += this.lineHeight
        } else if (invoice.resource && invoice.resource.firstName && invoice.resource.lastName) {
            this.doc.text(`${invoice.resource.firstName} ${invoice.resource.lastName}`, this.leftColumnX, this.currentY)
            this.currentY += this.lineHeight
        } else {
            this.doc.text(`Contractor ID: ${invoice.resourceID || 'N/A'}`, this.leftColumnX, this.currentY)
            this.currentY += this.lineHeight
        }

        if (invoice.resource && invoice.resource.email) {
            this.doc.text(invoice.resource.email, this.leftColumnX, this.currentY)
            this.currentY += this.lineHeight
        }

        this.currentY += 3

        // Project Information on the left
        this.doc.setFontSize(10)
        this.doc.setFont('helvetica', 'bold')
        this.doc.text('Project Information:', this.leftColumnX, this.currentY)

        this.currentY += 4

        this.doc.setFontSize(8)
        this.doc.setFont('helvetica', 'normal')

        if (invoice.billedAssemblyLine && invoice.billedAssemblyLine.name) {
            this.doc.text(invoice.billedAssemblyLine.name, this.leftColumnX, this.currentY)
            this.currentY += this.lineHeight
        } else {
            this.doc.text('Project: N/A', this.leftColumnX, this.currentY)
            this.currentY += this.lineHeight
        }

        if (invoice.billedAssemblyLine && invoice.billedAssemblyLine.startDate) {
            const startDate = new Date(invoice.billedAssemblyLine.startDate).toLocaleDateString()
            this.doc.text(`Start Date: ${startDate}`, this.leftColumnX, this.currentY)
            this.currentY += this.lineHeight
        }

        if (invoice.billingStart && invoice.billingEnd) {
            const billingStart = new Date(invoice.billingStart).toLocaleDateString()
            const billingEnd = new Date(invoice.billingEnd).toLocaleDateString()
            this.doc.text(`Billing Period: ${billingStart} - ${billingEnd}`, this.leftColumnX, this.currentY)
            this.currentY += this.lineHeight
        }

        this.currentY += this.sectionSpacing
    }

    private addDatesSection(invoice: PayoutInvoice): void {
        // Important Dates on the right (with proper spacing from Invoice details)
        this.doc.setFontSize(10)
        this.doc.setFont('helvetica', 'bold')
        this.doc.setTextColor(52, 73, 94)
        this.doc.text('Important Dates:', this.rightColumnX, this.margin + 35) // Add spacing from Invoice details

        this.doc.setFontSize(8)
        this.doc.setFont('helvetica', 'normal')

        const issueDate = invoice.issueDate ? new Date(invoice.issueDate).toLocaleDateString() :
            invoice.timestamp ? new Date(invoice.timestamp).toLocaleDateString() : 'N/A'
        const dueDate = invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : 'N/A'
        const billingPeriodStart = invoice.billingPeriodStart ||
            (invoice.billingStart ? new Date(invoice.billingStart).toLocaleDateString() : 'N/A')
        const billingPeriodEnd = invoice.billingPeriodEnd ||
            (invoice.billingEnd ? new Date(invoice.billingEnd).toLocaleDateString() : 'N/A')

        this.doc.text(`Issue Date: ${issueDate}`, this.rightColumnX, this.margin + 41)
        this.doc.text(`Due Date: ${dueDate}`, this.rightColumnX, this.margin + 45)
        this.doc.text(`Billing Period: ${billingPeriodStart} - ${billingPeriodEnd}`, this.rightColumnX, this.margin + 49)

        if (invoice.paidDate) {
            const paidDate = new Date(invoice.paidDate).toLocaleDateString()
            this.doc.text(`Paid Date: ${paidDate}`, this.rightColumnX, this.margin + 53)
        }

        this.currentY += this.sectionSpacing
    }

    private addDescriptionSection(invoice: PayoutInvoice): void {
        if (!invoice.description && !invoice.notes && !invoice.details) return

        this.doc.setFontSize(10)
        this.doc.setFont('helvetica', 'bold')
        this.doc.setTextColor(52, 73, 94)

        if (invoice.description) {
            this.doc.text('Description:', this.leftColumnX, this.currentY)
            this.currentY += 4

            this.doc.setFontSize(8)
            this.doc.setFont('helvetica', 'normal')
            this.doc.text(invoice.description, this.leftColumnX, this.currentY)
            this.currentY += this.sectionSpacing
        } else if (invoice.details) {
            this.doc.text('Details:', this.leftColumnX, this.currentY)
            this.currentY += 4

            this.doc.setFontSize(8)
            this.doc.setFont('helvetica', 'normal')
            this.doc.text(invoice.details, this.leftColumnX, this.currentY)
            this.currentY += this.sectionSpacing
        }

        if (invoice.notes) {
            this.doc.setFontSize(10)
            this.doc.setFont('helvetica', 'bold')
            this.doc.text('Internal Notes:', this.leftColumnX, this.currentY)
            this.currentY += 4

            this.doc.setFontSize(8)
            this.doc.setFont('helvetica', 'normal')
            this.doc.text(invoice.notes, this.leftColumnX, this.currentY)
            this.currentY += this.sectionSpacing
        }
    }

    private addLineItemsTable(invoice: PayoutInvoice): void {
        this.doc.setFontSize(10)
        this.doc.setFont('helvetica', 'bold')
        this.doc.setTextColor(52, 73, 94)
        this.doc.text('Line Items:', this.leftColumnX, this.currentY)

        this.currentY += 6

        // Create a line item from the available data
        const lineItems = invoice.lineItems || [{
            description: invoice.details || 'Contractor Services',
            type: 'SERVICE',
            quantity: invoice.billableUnits || 1,
            unit: 'units',
            unitPrice: invoice.billableAmount || 0,
            totalAmount: invoice.billableAmount || 0
        }]

        const tableData = lineItems.map(item => [
            item?.description || 'N/A',
            item?.type || 'N/A',
            item?.quantity?.toString() || '0',
            item?.unit || 'N/A',
            `$${item?.unitPrice?.toFixed(2) || '0.00'}`,
            `$${item?.totalAmount?.toFixed(2) || '0.00'}`
        ])

        this.doc.autoTable({
            startY: this.currentY,
            head: [['Description', 'Type', 'Qty', 'Unit', 'Unit Price', 'Total']],
            body: tableData,
            theme: 'grid',
            headStyles: {
                fillColor: [41, 128, 185],
                textColor: 255,
                fontSize: 7,
                fontStyle: 'bold'
            },
            bodyStyles: {
                fontSize: 6
            },
            columnStyles: {
                0: { cellWidth: 50 }, // Description
                1: { cellWidth: 25 }, // Type
                2: { cellWidth: 15, halign: 'center' }, // Qty
                3: { cellWidth: 20 }, // Unit
                4: { cellWidth: 25, halign: 'right' }, // Unit Price
                5: { cellWidth: 25, halign: 'right' }  // Total
            },
            margin: { left: this.leftColumnX, right: this.margin }
        })

        this.currentY = (this.doc as any).lastAutoTable.finalY + 6
    }

    private addTotalsSection(invoice: PayoutInvoice): void {
        const totalsX = this.pageWidth - this.margin - 80

        this.doc.setFontSize(8)
        this.doc.setFont('helvetica', 'normal')
        this.doc.setTextColor(52, 73, 94)

        // Subtotal
        this.doc.text('Subtotal:', totalsX, this.currentY)
        this.doc.text(`$${(invoice.subtotalAmount || invoice.billableAmount || 0).toFixed(2)}`, totalsX + 50, this.currentY, { align: 'right' })
        this.currentY += this.lineHeight

        // Tax
        this.doc.text('Tax:', totalsX, this.currentY)
        this.doc.text(`$${(invoice.taxAmount || 0).toFixed(2)}`, totalsX + 50, this.currentY, { align: 'right' })
        this.currentY += this.lineHeight

        // Divider
        this.doc.setDrawColor(200, 200, 200)
        this.doc.line(totalsX, this.currentY, totalsX + 70, this.currentY)
        this.currentY += this.lineHeight

        // Total
        this.doc.setFontSize(10)
        this.doc.setFont('helvetica', 'bold')
        this.doc.text('Total Amount:', totalsX, this.currentY)
        this.doc.text(`$${(invoice.totalAmount || invoice.billableAmount || 0).toFixed(2)}`, totalsX + 50, this.currentY, { align: 'right' })
        this.currentY += this.lineHeight + 2

        // Paid Amount
        this.doc.setFontSize(8)
        this.doc.setFont('helvetica', 'normal')
        this.doc.text('Paid Amount:', totalsX, this.currentY)
        this.doc.text(`$${(invoice.paidAmount || 0).toFixed(2)}`, totalsX + 50, this.currentY, { align: 'right' })
        this.currentY += this.lineHeight

        // Divider
        this.doc.setDrawColor(200, 200, 200)
        this.doc.line(totalsX, this.currentY, totalsX + 70, this.currentY)
        this.currentY += this.lineHeight

        // Amount Due
        this.doc.setFontSize(10)
        this.doc.setFont('helvetica', 'bold')
        const remainingAmount = invoice.remainingAmount ||
            (invoice.billableAmount - (invoice.paidAmount || 0)) || 0
        if (remainingAmount > 0) {
            this.doc.setTextColor(231, 76, 60) // Red for amount due
            this.doc.text('Amount Due:', totalsX, this.currentY)
            this.doc.text(`$${remainingAmount.toFixed(2)}`, totalsX + 50, this.currentY, { align: 'right' })
        } else {
            this.doc.setTextColor(46, 204, 113) // Green for fully paid
            this.doc.text('Fully Paid', totalsX, this.currentY)
            this.doc.text(`$${remainingAmount.toFixed(2)}`, totalsX + 50, this.currentY, { align: 'right' })
        }

        this.currentY += this.sectionSpacing
    }

    private addPaymentHistory(invoice: PayoutInvoice): void {
        if (!invoice.payments || invoice.payments.length === 0) return

        this.doc.setFontSize(10)
        this.doc.setFont('helvetica', 'bold')
        this.doc.setTextColor(52, 73, 94)
        this.doc.text('Payment History:', this.leftColumnX, this.currentY)

        this.currentY += 6

        const tableData = (invoice.payments || []).map(payment => [
            payment?.paymentDate ? new Date(payment.paymentDate).toLocaleDateString() : 'N/A',
            payment?.method || 'N/A',
            payment?.reference || 'N/A',
            `$${(payment?.amount || 0).toFixed(2)}`,
            payment?.status || 'N/A'
        ])

        this.doc.autoTable({
            startY: this.currentY,
            head: [['Date', 'Method', 'Reference', 'Amount', 'Status']],
            body: tableData,
            theme: 'grid',
            headStyles: {
                fillColor: [41, 128, 185],
                textColor: 255,
                fontSize: 7,
                fontStyle: 'bold'
            },
            bodyStyles: {
                fontSize: 6
            },
            columnStyles: {
                0: { cellWidth: 25 }, // Date
                1: { cellWidth: 25 }, // Method
                2: { cellWidth: 35 }, // Reference
                3: { cellWidth: 25, halign: 'right' }, // Amount
                4: { cellWidth: 20 }  // Status
            },
            margin: { left: this.leftColumnX, right: this.margin }
        })

        this.currentY = (this.doc as any).lastAutoTable.finalY + 6
    }

    private addFooter(): void {
        const footerY = this.doc.internal.pageSize.getHeight() - 20

        this.doc.setDrawColor(200, 200, 200)
        this.doc.line(this.margin, footerY, this.pageWidth - this.margin, footerY)

        this.doc.setFontSize(7)
        this.doc.setFont('helvetica', 'normal')
        this.doc.setTextColor(128, 128, 128)

        // Left side - Thank you message
        this.doc.text('Thank you for your business!', this.leftColumnX, footerY + 4)

        // Right side - Generation date
        this.doc.text(`Generated on ${new Date().toLocaleDateString()}`, this.pageWidth - this.margin, footerY + 4, { align: 'right' })
    }

    public generatePDF(invoice: PayoutInvoice): jsPDF {
        // Reset position
        this.currentY = this.margin

        // Add header section (company info + invoice details)
        this.addHeaderSection(invoice)

        // Add talent and project information (left column)
        this.addContractorAndProjectSection(invoice)

        // Add dates section (right column, aligned with left)
        this.addDatesSection(invoice)

        // Add description section (full width)
        this.addDescriptionSection(invoice)

        // Add line items table (full width)
        this.addLineItemsTable(invoice)

        // Add totals section (right side)
        this.addTotalsSection(invoice)

        // Add payment history if exists (full width)
        this.addPaymentHistory(invoice)

        // Add footer
        this.addFooter()

        return this.doc
    }

    public downloadPDF(invoice: PayoutInvoice, filename?: string): void {
        const doc = this.generatePDF(invoice)
        const fileName = filename || `${invoice.invoiceNumber || 'contractor-invoice'}.pdf`
        doc.save(fileName)
    }
}

// Helper function to download contractor invoice PDF
export const downloadContractorInvoicePDF = (invoice: PayoutInvoice, filename?: string): void => {
    const generator = new ContractorInvoicePDFGenerator()
    generator.downloadPDF(invoice, filename)
}
